// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing
// here ever talks to a server; the AI "generate cards from notes" call is in brain.ts
// and sends ONLY the notes text the user pasted (or the on-device OCR of a photo).
// Decks, cards, and the whole review history never leave the device.
//
// Data model:
//   deck        { id, name, description, created_at, updated_at, card_count, due_count, new_count }
//   card        { id, deck_id, front, back, tag, source, + FSRS scheduling state
//                 (state/due/stability/difficulty/reps/lapses/last_review — see fsrs.ts) }
//   review_log  { id, card_id, deck_id, grade 1..4, state_before, elapsed_days,
//                 scheduled_days, reviewed_at }

import { invoke } from "./tauri-web";
import {
  isDue, STATE_NEW, STATE_LEARNING, STATE_RELEARNING, STATE_REVIEW,
  type SchedulingState, type Grade,
} from "./fsrs";

export type CardSource = "typed" | "notes" | "photo";

export interface Deck {
  id: string;
  name: string;
  /** Optional free-text description. "" when not set. */
  description: string;
  created_at: string;
  updated_at: string;
  /** Computed on read. */
  card_count: number;
}

export type CardKind = "basic" | "cloze";

export interface Card extends SchedulingState {
  id: string;
  deck_id: string;
  /** The prompt side (a question / term / cue). */
  front: string;
  /** The answer side. */
  back: string;
  /** Short topic tag, normalized lowercase. "" when not set. */
  tag: string;
  /** How the card was made: typed by hand, from pasted notes, or from a photographed page. */
  source: CardSource;
  /** "basic" (front/back) or "cloze" (a sentence with one word blanked out). */
  kind: CardKind;
  created_at: string;
  updated_at: string;
}

export interface NewDeck { name: string; description?: string }
export type DeckPatch = Partial<{ name: string; description: string }>;

export interface NewCard {
  deck_id: string;
  front: string;
  back: string;
  tag?: string;
  source?: CardSource;
  kind?: CardKind;
}

export type CardPatch = Partial<{ front: string; back: string; tag: string; deck_id: string }>;

/** What gets persisted after grading a card — the new FSRS state + the log row. */
export interface ReviewInput {
  state: number;
  due: string;
  stability: number;
  difficulty: number;
  reps: number;
  lapses: number;
  last_review: string | null;
  grade: Grade;
  state_before: number;
  elapsed_days: number;
  scheduled_days: number;
  reviewed_at: string;
}

export interface ReviewLogEntry {
  id: string;
  card_id: string;
  deck_id: string;
  grade: number;
  state_before: number;
  elapsed_days: number;
  scheduled_days: number;
  reviewed_at: string;
}

export const store = {
  // ── Decks ──────────────────────────────────────────────────────────────────
  listDecks: () => invoke<Deck[]>("list_decks"),
  createDeck: (input: NewDeck) => invoke<Deck>("create_deck", { input }),
  updateDeck: (id: string, patch: DeckPatch) => invoke<void>("update_deck", { id, patch }),
  deleteDeck: (id: string) => invoke<void>("delete_deck", { id }),
  // ── Cards ──────────────────────────────────────────────────────────────────
  listCards: (deckId?: string, opts?: { limit?: number }) =>
    invoke<Card[]>("list_cards", { deckId: deckId ?? null, limit: opts?.limit ?? 50000 }),
  createCard: (input: NewCard) => invoke<Card>("create_card", { input }),
  /** Bulk-save (used by "generate from notes" — the user reviewed the preview first). */
  createCards: (deckId: string, cards: { front: string; back: string; tag?: string; source?: CardSource; kind?: CardKind }[]) =>
    invoke<number>("create_cards", { deckId, cards }),
  updateCard: (id: string, patch: CardPatch) => invoke<void>("update_card", { id, patch }),
  deleteCard: (id: string) => invoke<void>("delete_card", { id }),
  /** Persist a graded review: new FSRS scheduling state on the card + one review_log row. */
  applyReview: (id: string, review: ReviewInput) => invoke<void>("apply_review", { id, review }),
  listReviewLog: (deckId?: string, opts?: { limit?: number }) =>
    invoke<ReviewLogEntry[]>("list_review_log", { deckId: deckId ?? null, limit: opts?.limit ?? 5000 }),
  /** Persist a photographed notes page's image bytes on-device → returns its path. */
  savePhoto: (bytes: number[], ext: string) => invoke<string>("save_photo", { bytes, ext }),
  /** On-device OCR (Apple Vision) of a saved notes photo → recognized text. */
  ocrImage: (path: string) => invoke<string>("ocr_image", { path }),
};

// ── Pure policy / display helpers ──────────────────────────────────────────────

export const SOURCE_LABELS: Record<CardSource, string> = {
  typed: "Typed",
  notes: "From notes",
  photo: "From photo",
};

/** Normalize a tag — lowercase, trim, collapse internal whitespace. */
export function normalizeTag(raw: string): string {
  return raw.trim().toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}

/** Dedupe + preserve first-occurrence order; trims blanks. */
export function dedupeStrings(raw: Iterable<string>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of raw) {
    const t = r.trim();
    if (!t) continue;
    const key = normalizeTag(t);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Human "in x / x ago" for an ISO datetime, relative to now. */
export function relativeDue(iso: string | null, now: Date = new Date()): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diffMin = Math.round((t - now.getTime()) / 60000);
  const abs = Math.abs(diffMin);
  const unit = abs < 60 ? `${Math.max(1, abs)} min` : abs < 1440 ? `${Math.round(abs / 60)} h` : abs < 43200 ? `${Math.round(abs / 1440)} d` : `${Math.round(abs / 43200)} mo`;
  return diffMin <= 0 ? (abs < 2 ? "now" : `${unit} ago`) : `in ${unit}`;
}

// ── Deck stats (computed locally in TS over the cards + log; never sent anywhere) ──

export interface DeckStats {
  total: number;
  newCount: number;
  learning: number; // learning + relearning
  review: number;
  dueNow: number;
  reviewsTotal: number;
  /** Share of logged reviews graded Again (a lapse), 0..1. null when no reviews yet. */
  againRate: number | null;
}

export function computeDeckStats(cards: Card[], log: ReviewLogEntry[], now: Date = new Date()): DeckStats {
  let newCount = 0, learning = 0, review = 0, dueNow = 0;
  for (const c of cards) {
    if (c.state === STATE_NEW) newCount++;
    else if (c.state === STATE_LEARNING || c.state === STATE_RELEARNING) learning++;
    else if (c.state === STATE_REVIEW) review++;
    if (isDue(c, now)) dueNow++;
  }
  const again = log.filter((l) => l.grade === 1).length;
  return {
    total: cards.length,
    newCount, learning, review, dueNow,
    reviewsTotal: log.length,
    againRate: log.length > 0 ? again / log.length : null,
  };
}

/** The study queue: every card that is due now (new cards are due immediately), earliest first. */
export function buildStudyQueue(cards: Card[], now: Date = new Date(), newCap = 20): Card[] {
  const due = cards.filter((c) => isDue(c, now));
  const news = due.filter((c) => c.state === STATE_NEW).slice(0, newCap);
  const rest = due.filter((c) => c.state !== STATE_NEW);
  return [...rest, ...news].sort((a, b) => new Date(a.due).getTime() - new Date(b.due).getTime());
}

/**
 * Study streak (parity #14) — consecutive days with at least one review, counting back from today.
 * Pure date maths over `review_log`, which we already store: no AI call, nothing billed, works offline.
 * Today not yet studied does NOT break the streak (yesterday still counts) — a streak should survive
 * "I haven't studied yet this morning", which is exactly when people open the app.
 */
export interface StreakInfo {
  /** Consecutive days up to today (or yesterday if today is still empty). */
  current: number;
  /** Longest run ever, for the "best" line. */
  best: number;
  /** Did the customer already study today? Drives "keep it going" vs "done for today". */
  studiedToday: boolean;
  /** Last 7 days oldest→newest, true = studied. Feeds the little dot strip. */
  last7: boolean[];
}
export function computeStreak(log: ReviewLogEntry[], now: Date = new Date()): StreakInfo {
  const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const days = new Set<string>();
  for (const r of log) {
    const d = new Date(r.reviewed_at);
    if (!Number.isNaN(d.getTime())) days.add(dayKey(d));
  }
  const today = new Date(now); today.setHours(0, 0, 0, 0);
  const studiedToday = days.has(dayKey(today));
  // Count back from today; if today is empty, start at yesterday so the streak survives the morning.
  let current = 0;
  const cursor = new Date(today);
  if (!studiedToday) cursor.setDate(cursor.getDate() - 1);
  while (days.has(dayKey(cursor))) { current++; cursor.setDate(cursor.getDate() - 1); }
  // Best run across every recorded day.
  const sorted = [...days].map((k) => { const [y, m, d] = k.split("-").map(Number); return new Date(y, m, d).getTime(); }).sort((a, b) => a - b);
  let best = 0, run = 0, prev = 0;
  for (const t of sorted) {
    run = prev && t - prev === 86_400_000 ? run + 1 : 1;
    if (run > best) best = run;
    prev = t;
  }
  const last7: boolean[] = [];
  for (let i = 6; i >= 0; i--) { const d = new Date(today); d.setDate(d.getDate() - i); last7.push(days.has(dayKey(d))); }
  return { current, best: Math.max(best, current), studiedToday, last7 };
}
